import { inject, Injectable } from '@angular/core';
import {
  type HubConnection,
  HubConnectionBuilder,
  HubConnectionState,
  HttpError,
} from '@microsoft/signalr';
import { Observable, Subject } from 'rxjs';

import { ENVIRONMENT, PORTAL_AVAILABILITY } from '../../../tokens/app.tokens';
import type { IDxpHubConnectionProvider } from './i-dxp-hub-connection.provider';

/** I primi tentativi di riconnessione: partono uno dietro l'altro e non dicono niente a nessuno. */
export const HUB_SILENT_ATTEMPTS = 5;

/**
 * I tentativi dopo i silenziosi: ciascuno preceduto da un conto alla rovescia che chi usa il portale
 * vede. Finiti anche questi, la connessione resta giu' e lo si dice.
 */
export const HUB_COUNTED_ATTEMPTS = 5;

/** Da quanti secondi parte il conto alla rovescia prima di ciascun tentativo visibile. */
export const HUB_RETRY_COUNTDOWN_SECONDS = 10;

const ONE_SECOND_MS = 1000;

@Injectable()
export class SignalRDxpHubConnectionProvider implements IDxpHubConnectionProvider {
  private readonly env = inject(ENVIRONMENT);
  private readonly availability = inject(PORTAL_AVAILABILITY);
  private readonly closedSource = new Subject<void>();

  /** Il conto alla rovescia in corso prima di un tentativo visibile. */
  private countdownTimer: ReturnType<typeof setInterval> | undefined;

  private hubConnection: HubConnection | undefined;
  private hubSessionId: string | undefined;
  private hubLanguage: string | undefined;
  /** La lingua scritta nell'indirizzo: e' quella da cui la connessione riparte dopo una caduta. */
  private addressLanguage: string | undefined;

  /**
   * L'avvio in volo, conservato per chi arriva mentre e' in corso. Un booleano non basterebbe: due
   * ascoltatori che si iscrivono nello stesso tick lo troverebbero entrambi falso e partirebbero
   * entrambi.
   */
  private starting: Promise<void> | undefined;

  readonly closed$: Observable<void> = this.closedSource.asObservable();

  connection(sessionId?: string, language?: string): HubConnection {
    // Il sessionId sta nella query string: riusare la connessione di un'altra conversazione vorrebbe
    // dire ascoltare quella sbagliata, in silenzio. Chi non nomina una sessione invece si accontenta di
    // quella che c'e': la lista dell'operatore non ha una conversazione da seguire, e ricostruire la
    // connessione per lei butterebbe via quella della chat aperta accanto.
    if (sessionId !== undefined && this.hubConnection && this.hubSessionId !== sessionId) {
      this.stop();
    }

    // Chi arriva dopo e dichiara un'altra lingua non trova una connessione da costruire, ma una da
    // spostare: la postazione la apre per le assegnazioni, senza lingua, prima che la chat la dica.
    if (
      this.hubConnection &&
      sessionId !== undefined &&
      language !== undefined &&
      language !== this.hubLanguage
    ) {
      this.changeLanguage(sessionId, language).catch(() => undefined);
    }

    if (!this.hubConnection) {
      this.hubSessionId = sessionId;
      this.hubLanguage = language;
      this.addressLanguage = language;
      // Niente riconnessione automatica di SignalR: la politica dei tentativi e' una sola, la stessa per
      // una connessione che cade e per una che non parte, e sta in `connectWithRetries`.
      const hubConnection = new HubConnectionBuilder()
        .withUrl(this.hubUrlFor(sessionId, language), { withCredentials: true })
        .build();
      this.hubConnection = hubConnection;
      hubConnection.onclose(() => {
        // Fermata da noi — cambio di sessione, uscita — non e' una caduta: la connessione corrente e'
        // gia' un'altra, o nessuna. La vecchia si chiude quando il WebSocket lo dice, spesso mentre la
        // nuova sta ancora partendo, e niente di cio' che segue e' suo: toglierle l'avvio in volo
        // faceva passare `start()` su una connessione ancora `Connecting`.
        if (this.hubConnection !== hubConnection) return;

        this.starting = undefined;
        // Chi ascolta rilegge subito cio' che puo' rileggere per altre vie.
        this.closedSource.next();
        this.reconnect(hubConnection);
      });
    }

    return this.hubConnection;
  }

  start(sessionId?: string, language?: string): Promise<void> {
    const connection = this.connection(sessionId, language);

    // L'avvio in volo viene prima dello stato, non dopo: SignalR porta la connessione a `Connecting`
    // dentro `start()`, ma chi si iscrive nello stesso tick puo' arrivare mentre lo stato e' ancora
    // `Disconnected`. Fidarsi dello stato soltanto farebbe partire due avvii, che e' il difetto da cui
    // nasce tutto questo.
    if (this.starting) return this.starting;

    // Gia' in piedi: non c'e' niente da avviare e niente da aspettare.
    if (connection.state !== HubConnectionState.Disconnected) return Promise.resolve();

    const starting = this.connectWithRetries(connection).finally(() => {
      // Un avvio finito dopo un cambio di sessione non e' piu' quello in volo: libera solo il proprio.
      if (this.starting === starting) this.starting = undefined;
    });
    this.starting = starting;

    return starting;
  }

  /**
   * Una connessione caduta si riprende con gli stessi tentativi di una che non parte. Tornata, chi
   * ascolta rilegge cio' che e' arrivato durante il buco — gli ascoltatori registrati restano, la
   * connessione e' la stessa — e la lingua torna quella in cui si legge: l'indirizzo porta quella di
   * partenza.
   */
  private reconnect(connection: HubConnection): void {
    const starting = this.connectWithRetries(connection)
      .then(() => {
        this.closedSource.next();
        this.restoreLanguage();
      })
      .finally(() => {
        if (this.starting === starting) this.starting = undefined;
      });
    this.starting = starting;
    // Nessuno aspetta questa promessa, tranne chi si unisce dopo con `start`: a lui arriva il rifiuto.
    starting.catch(() => undefined);
  }

  /**
   * I tentativi, in tre tempi: {@link HUB_SILENT_ATTEMPTS} uno dietro l'altro senza dire niente — la
   * gran parte delle cadute e' un attimo, e un avviso che compare e sparisce spaventa soltanto — poi
   * {@link HUB_COUNTED_ATTEMPTS} ciascuno dopo un conto alla rovescia visibile, poi l'avviso fisso che
   * la connessione non c'e', con il comando per ricominciare da capo.
   *
   * Finiti i tentativi la promessa non si chiude ma aspetta quel comando: chi aspettava l'avvio resta in
   * attesa invece di ricevere un errore — che arriverebbe a schermo come un secondo avviso generico
   * sopra quello fisso — e viene servito quando la connessione torna.
   */
  private async connectWithRetries(connection: HubConnection): Promise<void> {
    while (!(await this.attemptCycle(connection))) {
      await this.retryRequested(connection);
      // Fermata da noi mentre si aspettava il comando: non c'e' piu' niente da riprovare.
      if (this.hubConnection !== connection) return;
    }
  }

  /**
   * Un giro di tentativi: vero se la connessione e' partita o se l'abbiamo fermata nel frattempo, falso
   * se sono finiti tutti. Un errore esce solo per un accesso negato.
   */
  private async attemptCycle(connection: HubConnection): Promise<boolean> {
    const attempts = HUB_SILENT_ATTEMPTS + HUB_COUNTED_ATTEMPTS;

    for (let attempt = 1; attempt <= attempts; attempt += 1) {
      if (attempt > HUB_SILENT_ATTEMPTS) {
        await this.countdown(connection);
        this.availability.reportRealtime({ phase: 'retrying' });
      }

      try {
        await connection.start();
        this.availability.reportRealtime({ phase: 'connected' });
        return true;
      } catch (error: unknown) {
        // Fermata da noi nel frattempo: chi la aspettava ascolta lei, e lei non ha piu' niente da dire.
        // L'avvio finisce in silenzio — un errore gli arriverebbe a schermo come «Qualcosa non ha
        // funzionato» — e non riprova.
        if (this.hubConnection !== connection) return true;
        this.rethrowIfNotRetriable(error);
      }
    }

    return false;
  }

  /**
   * Cio' che riprovare non aggiusta: un biglietto scaduto o un accesso negato restano all'errore di chi
   * ha chiesto l'avvio.
   */
  private rethrowIfNotRetriable(error: unknown): void {
    if (isRejectedAccess(error)) {
      this.availability.reportRealtime({ phase: 'connected' });
      // L'hub rifiuta con 403 solo l'operatore non abilitato sull'ente da cui accede (un biglietto
      // scaduto e' 401): la postazione intera non si apre, e lo dice la radice.
      if (isForbidden(error)) this.availability.reportOperatorNotEnabled();
      throw error;
    }
  }

  /**
   * L'avviso fisso, e l'attesa del suo comando. Chiesto da chi usa il portale si ricomincia da capo, e
   * mentre i tentativi silenziosi girano l'avviso dice subito che si sta provando: il comando ha
   * risposto.
   */
  private retryRequested(connection: HubConnection): Promise<void> {
    return new Promise<void>((resume) => {
      this.availability.reportRealtime({
        phase: 'failed',
        retry: () => {
          if (this.hubConnection === connection) {
            this.availability.reportRealtime({ phase: 'retrying' });
          }
          resume();
        },
      });
    });
  }

  /**
   * Il conto alla rovescia prima di un tentativo visibile, un secondo alla volta. Se nel frattempo la
   * connessione viene fermata non arriva mai a zero: il tentativo non ha piu' ragione di partire.
   */
  private countdown(connection: HubConnection): Promise<void> {
    return new Promise((resolve) => {
      let secondsLeft = HUB_RETRY_COUNTDOWN_SECONDS;
      this.availability.reportRealtime({ phase: 'waiting', secondsLeft });

      this.countdownTimer = setInterval(() => {
        if (this.hubConnection !== connection) {
          clearInterval(this.countdownTimer);
          return;
        }

        secondsLeft -= 1;
        if (secondsLeft > 0) {
          this.availability.reportRealtime({ phase: 'waiting', secondsLeft });
          return;
        }

        clearInterval(this.countdownTimer);
        this.countdownTimer = undefined;
        resolve();
      }, ONE_SECOND_MS);
    });
  }

  /** Tornata dopo una caduta, la connessione rientra con la lingua dell'indirizzo. */
  private restoreLanguage(): void {
    const current = this.hubLanguage;
    if (!this.hubSessionId || !current || current === this.addressLanguage) return;

    this.hubConnection
      ?.invoke('changeLanguageAsync', this.hubSessionId, current)
      .catch(() => undefined);
  }

  /**
   * Senza sessione l'indirizzo e' quello nudo: il BFF legge il `sessionId` dalla query string per far
   * entrare la connessione nel gruppo della conversazione, e non averlo significa non entrarci.
   */
  private hubUrlFor(sessionId?: string, language?: string): string {
    if (sessionId === undefined) return `${this.env.hubUrl}/dxp`;

    const query = new URLSearchParams({ sessionId });
    // La lingua del cittadino la dice comunque il biglietto di sessione: dichiararla serve
    // all'operatore, che nel biglietto non ce l'ha, e non fa danno all'altro.
    if (language !== undefined) query.set('language', language);

    return `${this.env.hubUrl}/dxp?${query.toString()}`;
  }

  /**
   * Un cambio di sotto-gruppo, non una riconnessione: la connessione resta la stessa e la conversazione
   * non perde chi la stava seguendo. Se non c'e' ancora una connessione non c'e' niente da spostare — la
   * lingua entrera' nell'indirizzo quando verra' aperta.
   */
  async changeLanguage(sessionId: string, language: string): Promise<void> {
    if (this.hubLanguage === language) return;

    const previous = this.hubLanguage;
    this.hubLanguage = language;

    if (!this.hubConnection) return;

    try {
      await this.start(sessionId, language);
      await this.hubConnection.invoke('changeLanguageAsync', sessionId, language);
    } catch (error: unknown) {
      // Rifiutato non e' avvenuto: chi lo chiede di nuovo deve poterlo riprovare.
      this.hubLanguage = previous;
      throw error;
    }
  }

  connectionId(): string | undefined {
    return this.hubConnection?.connectionId ?? undefined;
  }

  stop(): void {
    clearInterval(this.countdownTimer);
    this.countdownTimer = undefined;
    // Fermata da noi non e' una caduta: gli avvisi della connessione vecchia non valgono piu'.
    if (this.hubConnection) this.availability.reportRealtime({ phase: 'connected' });
    void this.hubConnection?.stop();
    this.hubConnection = undefined;
    this.hubSessionId = undefined;
    this.hubLanguage = undefined;
    this.addressLanguage = undefined;
    this.starting = undefined;
  }
}

function isForbidden(error: unknown): boolean {
  return error instanceof HttpError && error.statusCode === 403;
}

function isRejectedAccess(error: unknown): boolean {
  return error instanceof HttpError && (error.statusCode === 401 || error.statusCode === 403);
}
